import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

interface KpiCard {
  label: string;
  value: string;
  icon: string;
  change: string;
  changePositive: boolean;
  variant: 'revenue' | 'fees' | 'payroll' | 'overdue';
}

interface CollectionItem {
  name: string;
  collected: string;
  total: string;
  percent: number;
  color: 'teal' | 'indigo' | 'amber' | 'maroon';
}

interface Transaction {
  name: string;
  initials: string;
  studentId: string;
  type: string;
  amount: number;
  status: 'Paid' | 'Pending' | 'Overdue' | 'Partial';
}

interface QuickAction {
  label: string;
  icon: string;
  route: string;
}

interface BalanceStat {
  label: string;
  value: string;
  sub: string;
  urgent: boolean;
}

interface OverdueItem {
  name: string;
  daysOverdue: number;
  amount: number;
}

@Component({
  selector: 'app-financedashboard',
  templateUrl: './financedashboard.component.html',
  styleUrls: ['./financedashboard.component.scss']
})
export class FinancedashboardComponent implements OnInit {
  public loading = false;
  currentUser: any;

  periods = ['This Semester', 'Last Semester', 'This Year'];
  activePeriod = 'This Semester';
  lastUpdated = 'Just now';

  kpiCards: KpiCard[] = [
    {
      label: 'Total Revenue',
      value: 'GH₵ 0.00',
      icon: 'account_balance',
      change: '+12.4%',
      changePositive: true,
      variant: 'revenue'
    },
    {
      label: 'Fees Collected',
      value: 'GH₵ 0.00',
      icon: 'payments',
      change: '+8.1%',
      changePositive: true,
      variant: 'fees'
    },
    {
      label: 'Payroll Disbursed',
      value: 'GH₵ 0.00',
      icon: 'attach_money',
      change: '0.0%',
      changePositive: false,
      variant: 'payroll'
    },
    {
      label: 'Overdue Fees',
      value: 'GH₵ 0.00',
      icon: 'warning',
      change: '0.00%',
      changePositive: true,
      variant: 'overdue'
    }
  ];

  collectionItems: CollectionItem[] = [
    { name: 'Application Form Fee', collected: 'GH₵ 0.00',   total: 'GH₵ 0.00', percent: 0, color: 'maroon' },
    { name: 'Tuition Fees',         collected: 'GH₵ 0.00',   total: 'GH₵ 0.00', percent: 0, color: 'teal'   },
    { name: 'Hostel Fees',          collected: 'GH₵ 88,500', total: 'GH₵ 0.00', percent: 0, color: 'indigo' },
    { name: 'Lab & Facility Fees',  collected: 'GH₵ 62,100', total: 'GH₵ 0.00', percent: 0, color: 'amber'  },
    { name: 'Registration Fees',    collected: 'GH₵ 55,600', total: 'GH₵ 0.00', percent: 0, color: 'teal'   },
    { name: 'Library Fees',         collected: 'GH₵ 18,400', total: 'GH₵ 0.00', percent: 0, color: 'maroon' },
  ];

  recentTransactions: Transaction[] = [
    { name: 'Ama Owusu',       initials: 'AO', studentId: 'TUC/2024/0341', type: 'Tuition',       amount: 3200, status: 'Paid'    },
    { name: 'Kwame Asante',    initials: 'KA', studentId: 'TUC/2024/0198', type: 'Accommodation', amount: 1800, status: 'Partial' },
    { name: 'Akosua Frimpong', initials: 'AF', studentId: 'TUC/2024/0612', type: 'Tuition',       amount: 3200, status: 'Pending' },
  ];

  quickActions: QuickAction[] = [
    { label: 'Record Payment',    icon: 'add_circle',  route: '/fee-management'    },
    { label: 'Generate Invoice',  icon: 'receipt',     route: '/student-accounts'  },
    { label: 'View Scholarships', icon: 'school',      route: '/scholarships'      },
    { label: 'Budget Report',     icon: 'pie_chart',   route: '/budget-management' },
    { label: 'Run Payroll',       icon: 'payments',    route: '/payroll'           },
    { label: 'Financial Report',  icon: 'assessment',  route: '/financial-reports' },
  ];

  balanceStats: BalanceStat[] = [
    { label: 'Total Outstanding', value: 'GH₵ 93,400', sub: 'Across all students', urgent: true  },
    { label: 'Students Owing',    value: '147',         sub: 'Active accounts',     urgent: false },
    { label: 'Avg. Balance',      value: 'GH₵ 635',    sub: 'Per student owing',   urgent: false },
  ];

  overdueList: OverdueItem[] = [
    { name: 'Efua Mensah',   daysOverdue: 42, amount: 3200 },
    { name: 'Kofi Antwi',    daysOverdue: 35, amount: 2800 },
    { name: 'Abena Darko',   daysOverdue: 28, amount: 1600 },
    { name: 'Samuel Tetteh', daysOverdue: 21, amount: 4050 },
    { name: 'Grace Asiedu',  daysOverdue: 14, amount: 960  },
  ];

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.loadUserData();
    this.loadDashboardData();
    this.lastUpdated = new Date().toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' });
  }

  setPeriod(period: string): void {
    this.activePeriod = period;
    // Hook: reload data based on selected period
  }

  navigateTo(route: string): void {
    if (route) {
      this.router.navigate([route]);
    }
  }

  loadUserData(): void {
    const userStr = localStorage.getItem('sysadmin_user');
    this.currentUser = userStr ? JSON.parse(userStr) : { username: 'Registrar' };
  }

  loadDashboardData(): void {
    this.loading = true;
    // API call here
    setTimeout(() => {
      this.loading = false;
    }, 1000);
  }
}